import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2, AlertTriangle, AlertCircle, Activity, RefreshCcw, Calendar,
  HardDrive, HandCoins, UsersRound, Send, Database, Clock, Landmark,
} from "lucide-react";

const SERVICE_META = {
  google_calendar:    { label: "Google Calendar",    icon: Calendar },
  google_drive:       { label: "Google Drive",       icon: HardDrive },
  pushpay_giving:     { label: "Pushpay Giving",     icon: HandCoins },
  pushpay_chms:       { label: "Pushpay ChMS",       icon: UsersRound },
  pushpay_scheduler:  { label: "Pushpay Scheduler",  icon: UsersRound },
  mailchimp:          { label: "Mailchimp",          icon: Send },
  quickbooks:         { label: "QuickBooks (Finance)", icon: Landmark },
  backups:            { label: "Nightly Backups",    icon: Database },
};

const STATUS_TONE = {
  ok:              { wash: "bg-sage/12", text: "text-sage", dot: "bg-sage", icon: CheckCircle2, label: "Healthy" },
  degraded:        { wash: "bg-gold/15", text: "text-gold", dot: "bg-gold", icon: AlertTriangle, label: "Degraded" },
  // Non-blocking informational state. Looks like "degraded" but reads as
  // "Limited" so admins know it's a known limitation, not a failure.
  warning:         { wash: "bg-gold/12", text: "text-gold/90", dot: "bg-gold/80", icon: AlertTriangle, label: "Limited" },
  down:            { wash: "bg-terracotta/12", text: "text-terracotta", dot: "bg-terracotta", icon: AlertCircle, label: "Down" },
  not_configured:  { wash: "bg-brand-primary/8", text: "text-brand-primary/55", dot: "bg-brand-primary/30", icon: AlertCircle, label: "Not configured" },
};

function relativeTime(iso) {
  if (!iso) return "—";
  try {
    const diff = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    return `${Math.round(diff / 86400)}d ago`;
  } catch { return "—"; }
}

export default function SystemHealthPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setLoading(data === null);
    setRefreshing(data !== null);
    try {
      const { data: res } = await api.get("/system/health");
      setData(res);
    } catch {
      setData({ overall: "down", services: {}, checked_at: null });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    // Auto-refresh every 60s while the page is open.
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, []);

  const overall = STATUS_TONE[data?.overall] || STATUS_TONE.not_configured;

  return (
    <Card data-testid="system-health-card" className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-6">
      <div className="flex items-start justify-between mb-5 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className={`h-11 w-11 rounded-xl ${overall.wash} grid place-items-center`}>
            <Activity className={`h-5 w-5 ${overall.text}`} strokeWidth={1.7} />
          </div>
          <div>
            <p className="font-heading text-lg text-brand-primary">System health</p>
            <p className="text-sm text-brand-primary/60">
              {data?.checked_at ? <>All integrations at a glance · checked {relativeTime(data.checked_at)}</>
                                : "Live status of every dashboard integration."}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${overall.wash} ${overall.text}`}
            data-testid="system-health-overall"
          >
            <span className={`h-1.5 w-1.5 rounded-full ${overall.dot}`} />
            {overall.label}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={refreshing}
            data-testid="system-health-refresh"
            className="h-9 rounded-full"
          >
            <RefreshCcw className={`h-3.5 w-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-20 rounded-xl bg-cream/50 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {Object.entries(data?.services || {}).map(([key, svc]) => {
            const meta = SERVICE_META[key] || { label: key, icon: Activity };
            const tone = STATUS_TONE[svc.status] || STATUS_TONE.not_configured;
            const Icon = meta.icon;
            const ToneIcon = tone.icon;
            return (
              <div
                key={key}
                data-testid={`system-health-service-${key}`}
                className={`flex items-start gap-3 rounded-xl border border-brand-primary/10 bg-white p-4 hover:shadow-warm transition-shadow`}
              >
                <div className={`h-9 w-9 rounded-lg ${tone.wash} grid place-items-center shrink-0`}>
                  <Icon className={`h-4 w-4 ${tone.text}`} strokeWidth={1.7} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="font-medium text-brand-primary text-sm">{meta.label}</p>
                    <span className={`inline-flex items-center gap-1 text-xs ${tone.text}`}>
                      <ToneIcon className="h-3 w-3" /> {tone.label}
                    </span>
                  </div>
                  <p className="text-xs text-brand-primary/65 mt-0.5 truncate">{svc.message || "—"}</p>
                  <div className="flex items-center gap-3 text-[11px] text-brand-primary/50 mt-1.5 flex-wrap">
                    {svc.last_sync_at && (
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3 w-3" /> {relativeTime(svc.last_sync_at)}
                      </span>
                    )}
                    {svc.last_error && (
                      <span className="text-terracotta truncate" title={svc.last_error}>
                        ⚠ {String(svc.last_error).slice(0, 60)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
